import * as Sentry from "@sentry/browser";
import { FormikErrors, FormikTouched, useFormik } from "formik";
import React, {
  Fragment,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from "react";

import { Column, Container, Dialog, Icon, Pressable, Row } from "../components";
import { PaymentInput } from "../core";
import { useI18n } from "../lib/i18n/i18n_provider";
import { OfferingMessageType, useValidatePromoQuery } from "./api";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { CheckoutInput } from "./checkout";
import {
  LetsTalkConfirmation,
  LetsTalkForm,
  QuestionareData,
} from "./contact_form_provider";
import { countries } from "./countries";
import { Grid } from "./grid";
import { Heading } from "./heading";
import { DayPassDateRange, MeetingRoomDateRange } from "./order_params";
import { PickerFormField } from "./picker_form_field";
import { Spacer } from "./spacer";
import { Text } from "./text";
import { TextFormField } from "./text_form_field";
import { TextLink } from "./text_link";
import { CenteredPulseLoader } from "../components/centered_pulse_loader";
import { useAnalytics } from "@/lib/analytics/analytics";
import { usePrevious } from "@/hooks/use_previous";
import { isEmail, toDigitsOnly } from "@/lib/validation/string";
import { useHubspotFormToUse } from "@/hooks";
import { useTalkToUsState } from "@/hooks/use_talk_to_us_state";
import { useWeWorkExternalContactUrl } from "./hooks/use_wework_external_contact_url";
import { useRecentSignupEmailPrefill } from "@/offering_v2/hooks/use_recent_signup_email_prefill";
import { joinNameParts } from "@/lib/js_utils/name_utils";

export interface OfferingParams {
  fromCatalog?: string;
}

interface GuestDetailsInput {
  fullName: string;
  firstName?: string;
  lastName?: string;
  companyName: string;
  phoneNumber: string;
  email: string;
  bookingReason?: string;
}

interface GuestDetailsProps {
  askForBookingReason?: boolean;
  requireLastName?: boolean;
  values: GuestDetailsInput;
  errors: FormikErrors<GuestDetailsInput>;
  touched: FormikTouched<GuestDetailsInput>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  onChangeEmail: (value?: string) => void;
}

export function GuestDetails(props: GuestDetailsProps) {
  const {
    askForBookingReason,
    requireLastName,
    values,
    touched,
    errors,
    setFieldValue,
    onChangeEmail,
  } = props;
  const analytics = useAnalytics();
  const { fbt } = useI18n();
  const [email, setEmail] = useState<string>();
  const prevEmail = usePrevious(values.email);

  // Analytics identifies the guest from fullName while the form is being
  // filled in, so it has to hold the name even when it is asked in two parts.
  const onChangeNamePart = (field: "firstName" | "lastName", value: string) => {
    const parts = {
      firstName: values.firstName ?? "",
      lastName: values.lastName ?? "",
      [field]: value,
    };
    // Formik validates each change against the values of the last render, so
    // the change that validates has to be the one to the typed field, or its
    // error is judged against the field's previous value. The joined name is
    // never validated while the two parts are asked for.
    setFieldValue(
      "fullName",
      joinNameParts(parts.firstName, parts.lastName),
      false
    );
    setFieldValue(field, value);
  };

  const onChangeGuestEmail = useCallback(
    (email: string) => {
      setEmail(email);
      setFieldValue("email", email);
      const filledByBrowserAutofill = email.length - prevEmail.length > 1;

      if (filledByBrowserAutofill && email !== prevEmail) {
        Sentry.setUser({ email });
        analytics.identify("", {
          email,
          name: values.fullName,
          source: "WL",
        });
      }
    },
    [analytics, setFieldValue, prevEmail, values.fullName]
  );

  // Prefill from a recent discount popup signup. onChangeGuestEmail updates
  // local + Formik state; onChangeEmail notifies the parent (its `email` state
  // is what flows into the PromoCode validation call, otherwise the server
  // returns "Please enter your email address before entering a promo code").
  useRecentSignupEmailPrefill(values.email, (email) => {
    onChangeGuestEmail(email);
    onChangeEmail(email);
  });

  const onEmailFocusOut = useCallback(() => {
    onChangeEmail(email);

    if (email && isEmail(email) && email !== prevEmail) {
      Sentry.setUser({ email });
      analytics.identify("", {
        email: email,
        name: values.fullName,
        source: "WL",
      });
    }
  }, [email, onChangeEmail, prevEmail, values.fullName, analytics]);

  return (
    <Container>
      <Heading>{fbt("Your details")}</Heading>
      <Spacer size={8} />
      <Grid itemsPerRow={{ md: 2, default: 1 }} spacing={20}>
        {requireLastName && (
          <Container paddingBottom={16}>
            <TextFormField
              onChange={(firstName) => onChangeNamePart("firstName", firstName)}
              value={values.firstName ?? ""}
              label={fbt("First Name")}
              name="firstName"
              error={touched.firstName && errors.firstName}
            />
          </Container>
        )}
        {requireLastName && (
          <Container paddingBottom={16}>
            <TextFormField
              onChange={(lastName) => onChangeNamePart("lastName", lastName)}
              value={values.lastName ?? ""}
              label={fbt("Last Name")}
              name="lastName"
              error={touched.lastName && errors.lastName}
            />
          </Container>
        )}
        {!requireLastName && (
          <Container paddingBottom={16}>
            <TextFormField
              onChange={(fullName) => setFieldValue("fullName", fullName)}
              value={values.fullName}
              label={fbt("Full name")}
              name="fullName"
              error={touched.fullName && errors.fullName}
            />
          </Container>
        )}
        <Container paddingBottom={16}>
          <TextFormField
            onChange={(phoneNumber) =>
              setFieldValue("phoneNumber", toDigitsOnly(phoneNumber))
            }
            value={values.phoneNumber}
            label={fbt("Phone number")}
            name="phoneNumber"
            error={touched.phoneNumber && errors.phoneNumber}
          />
        </Container>
        <Container paddingBottom={16}>
          <TextFormField
            onChange={onChangeGuestEmail}
            onFocusOut={onEmailFocusOut}
            value={values.email}
            label={fbt("Email")}
            name="email"
            error={touched.email && errors.email}
            direction="ltr"
          />
        </Container>
      </Grid>
      <Grid itemsPerRow={{ md: 2, default: 1 }} spacing={20}>
        {askForBookingReason && (
          <Container paddingBottom={16}>
            <TextFormField
              onChange={(bookingReason) =>
                setFieldValue("bookingReason", bookingReason)
              }
              value={values.bookingReason || ""}
              label={fbt("bookingReasonTitle")}
              name="bookingReason"
              error={touched.bookingReason && errors.bookingReason}
            />
          </Container>
        )}
      </Grid>
    </Container>
  );
}

export interface BillingDetailsInput {
  companyName: string;
  billingStreetAddress: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
  billingTax: string;
}

interface BillingDetailsProps {
  askForTax: boolean;
  requireBillingState?: boolean;
  values: BillingDetailsInput;
  errors: FormikErrors<BillingDetailsInput>;
  touched: FormikTouched<BillingDetailsInput>;
  setFieldValue: (field: string, value: any) => void;
}

export function BillingDetails(props: BillingDetailsProps) {
  const {
    askForTax,
    requireBillingState,
    values,
    touched,
    errors,
    setFieldValue,
  } = props;
  const { fbt } = useI18n();
  return (
    <Container>
      <Heading>{fbt("Billing details")}</Heading>
      <Spacer size={8} />
      <Grid itemsPerRow={{ md: 2, default: 1 }} spacing={20}>
        <Container paddingBottom={16}>
          <TextFormField
            onChange={(companyName) =>
              setFieldValue("companyName", companyName)
            }
            value={values.companyName}
            label={fbt("Company Name")}
            name="companyName"
            error={touched.companyName && errors.companyName}
          />
        </Container>
        <Container paddingBottom={16}>
          <TextFormField
            onChange={(billingStreetAddress) =>
              setFieldValue("billingStreetAddress", billingStreetAddress)
            }
            value={values.billingStreetAddress}
            label={fbt("Street address")}
            name="billingStreetAddress"
            error={touched.billingStreetAddress && errors.billingStreetAddress}
          />
        </Container>
        <Container paddingBottom={16}>
          <TextFormField
            onChange={(billingCity) =>
              setFieldValue("billingCity", billingCity)
            }
            value={values.billingCity}
            label={fbt("City / Town")}
            name="billingCity"
            error={touched.billingCity && errors.billingCity}
          />
        </Container>
        <Container paddingBottom={16}>
          <TextFormField
            onChange={(billingState) =>
              setFieldValue("billingState", billingState)
            }
            value={values.billingState}
            label={
              requireBillingState
                ? fbt("State / Province / Region")
                : fbt("State / Province / Region (optional)")
            }
            name="billingState"
            error={touched.billingState && errors.billingState}
          />
        </Container>
        <Container paddingBottom={16}>
          <TextFormField
            onChange={(billingPostalCode) =>
              setFieldValue("billingPostalCode", billingPostalCode)
            }
            value={values.billingPostalCode}
            label={fbt("Postal code")}
            name="billingPostalCode"
            error={touched.billingPostalCode && errors.billingPostalCode}
          />
        </Container>
        <Container paddingBottom={16}>
          <PickerFormField
            onChange={(billingCountry) =>
              setFieldValue("billingCountry", billingCountry)
            }
            value={values.billingCountry}
            options={countries}
            label={fbt("Country")}
            name="billingCountry"
            error={touched.billingCountry && errors.billingCountry}
          />
        </Container>
        {askForTax && (
          <Container paddingBottom={16}>
            <TextFormField
              onChange={(billingTax) => setFieldValue("billingTax", billingTax)}
              value={values.billingTax}
              label={fbt("Tax / VAT / ID number")}
              name="billingTax"
              error={touched.billingTax && errors.billingTax}
            />
          </Container>
        )}
      </Grid>
    </Container>
  );
}

interface PaymentDetailsProps {
  values: CheckoutInput;
  errors: FormikErrors<CheckoutInput>;
  touched: FormikTouched<CheckoutInput>;
  setFieldValue: (field: string, value: any) => void;
  setFieldError: (field: string, value: any) => void;
}

export function PaymentDetails(props: PaymentDetailsProps) {
  const { errors, touched, setFieldError, setFieldValue } = props;
  const { fbt } = useI18n();
  const handlePaymentInputChange = React.useCallback(
    (complete: boolean, error?: string) => {
      setFieldValue("paymentComplete", complete);

      if (error) {
        setFieldError("paymentComplete", error);
      } else {
        setFieldError("paymentComplete", undefined);
      }
    },
    [setFieldValue, setFieldError]
  );

  return (
    <Container>
      <Heading>{fbt("Payment details")}</Heading>
      <Spacer size={8} />
      <PaymentInput onChange={handlePaymentInputChange} />
      {touched.paymentComplete && errors.paymentComplete && (
        <>
          <Spacer size={8} />
          <Text color="error">{errors.paymentComplete}</Text>
        </>
      )}
    </Container>
  );
}

interface PaymentCTAProps {
  termsURL?: string;
  onSubmit: () => void;
  disabled: boolean;
  isPaymentRequired?: boolean;
}

export function PaymentCTA(props: PaymentCTAProps) {
  const { onSubmit, disabled, termsURL, isPaymentRequired = true } = props;
  const [open, setOpen] = useState(false);
  const { fbt } = useI18n();
  return (
    <Container maxWidth={340}>
      <Button
        disabled={disabled}
        onPress={onSubmit}
        icon="lock"
        title={isPaymentRequired ? fbt("Confirm and Pay") : fbt("Confirm")}
      />
      <Spacer size={16} />
      {termsURL && (
        <Fragment>
          <Text>
            {fbt("By clicking on Confirm and Pay I agree to the")}{" "}
            <TextLink onPress={() => setOpen(true)} color="default">
              {fbt("terms and conditions")}
            </TextLink>
            .
          </Text>
          <Dialog isOpen={open} onRequestClose={() => setOpen(false)}>
            <div className="offering-checkout-dialog-body">
              <Pressable onPress={() => setOpen(false)}>
                <Icon name="x" />
              </Pressable>
              <div className="offering-checkout-iframe-container">
                <iframe
                  className="offering-checkout-iframe"
                  title="offering-checkout-iframe"
                  src={termsURL}
                />
              </div>
            </div>
          </Dialog>
        </Fragment>
      )}
    </Container>
  );
}

interface TermsInput {
  termsChecked?: boolean;
}

interface PaymentCTAWithTermsProps {
  terms: string;
  onSubmit: () => void;
  disabled: boolean;
  isPaymentRequired: boolean;
  values: TermsInput;
  errors: FormikErrors<TermsInput>;
  setFieldValue: (field: string, value: any) => void;
}

export function PaymentCTAWithTerms(props: PaymentCTAWithTermsProps) {
  const {
    values,
    errors,
    setFieldValue,
    onSubmit,
    disabled,
    terms,
    isPaymentRequired,
  } = props;
  const { fbt } = useI18n();
  const [open, setOpen] = useState(false);
  const [termsUrl, setTermsUrl] = useState();
  /** This function to find the URL in the terms and conditions
   * To handle display this url in an iframe dialog
   */
  const contentClickHandler = useCallback((e: any) => {
    const targetLink = e.target.closest("a");
    if (!targetLink) return;
    if (targetLink.target === "_blank") return;

    e.preventDefault();
    setTermsUrl(targetLink);
    setOpen(true);
  }, []);

  return (
    <Container>
      <div className="terms-and-conditions">
        <Container>
          <Row>
            <Container padding={16} center>
              <Checkbox
                value={values.termsChecked}
                onChange={(v) => setFieldValue("termsChecked", v)}
              />
            </Container>
            <Container flex={1}>
              <Text>
                <div
                  className="terms-content"
                  onClick={contentClickHandler}
                  dangerouslySetInnerHTML={{ __html: terms }}
                />
              </Text>
            </Container>
          </Row>
        </Container>
      </div>
      <Spacer size={8} />
      {errors.termsChecked && <Text color="error">{errors.termsChecked}</Text>}
      <Spacer size={16} />
      <Button
        disabled={disabled}
        onPress={onSubmit}
        icon="lock"
        title={isPaymentRequired ? fbt("Confirm and Pay") : fbt("Confirm")}
      />
      <Spacer size={16} />
      <Dialog isOpen={open} onRequestClose={() => setOpen(false)}>
        <div className="offering-checkout-dialog-body">
          <Pressable onPress={() => setOpen(false)}>
            <Icon name="x" />
          </Pressable>
          <div className="offering-checkout-iframe-container">
            <iframe
              className="offering-checkout-iframe"
              title="offering-checkout-iframe"
              src={termsUrl}
            />
          </div>
        </div>
      </Dialog>
    </Container>
  );
}

interface PromotionProps {
  partnerID: string;
  promotableType: string;
  promotableID?: string;
  email?: string;
  quantity?: number;
  dayPassDateRange?: DayPassDateRange;
  timeRange?: MeetingRoomDateRange;
  onChangePromoCode: (value: string) => void;
  partnerOfferingID?: string;
}

export function Promotion(props: PromotionProps) {
  const {
    partnerID,
    promotableType,
    promotableID,
    quantity,
    email,
    onChangePromoCode,
    dayPassDateRange,
    timeRange,
    partnerOfferingID,
  } = props;
  const [open, setOpen] = useState(false);
  const validatePromo = useValidatePromoQuery();
  const { fbt } = useI18n();
  const { setFieldValue, values, submitForm, setFieldError, errors } =
    useFormik({
      initialValues: {
        code: "",
      },
      onSubmit: async (v, { setErrors, setSubmitting }) => {
        if (!promotableID) {
          return;
        }

        const result = await validatePromo(
          encodeURIComponent(v.code),
          partnerID,
          promotableType,
          promotableID,
          email,
          dayPassDateRange?.startDate,
          dayPassDateRange?.endDate,
          timeRange?.date,
          timeRange?.startTime,
          timeRange?.endTime,
          quantity,
          undefined,
          partnerOfferingID
        );

        if (result.valid) {
          onChangePromoCode(v.code);
          setSubmitting(false);
          setOpen(false);
        } else {
          setSubmitting(false);
          if (result.errors?.length > 0) {
            result.errors.forEach((message: string) => {
              setErrors({
                code: message,
              });
            });
          } else {
            setErrors({
              code: fbt("The promotion code is invalid or has expired."),
            });
          }
        }
      },
      validateOnChange: false,
      validateOnBlur: false,
      validate: (v) => {
        const errors: { [field: string]: string } = {};

        if (!v.code) {
          errors.code = "Please add promotion code";
        }

        return errors;
      },
    });

  useEffect(() => {
    setFieldValue("code", "");
    setFieldError("code", undefined);
  }, [partnerID, promotableType, promotableID, setFieldError, setFieldValue]);

  return (
    <Container maxWidth={340}>
      <TextLink onPress={() => setOpen(true)}>
        {fbt("Enter a promo code")}
      </TextLink>
      <Spacer size={16} />
      <Dialog isOpen={open} onRequestClose={() => setOpen(false)}>
        <div className="offering-checkout-promotion-dialog-body">
          <Pressable onPress={() => setOpen(false)}>
            <Icon name="x" />
          </Pressable>
          <Heading>{fbt("Promo code")}</Heading>
          <Spacer size={16} />
          <TextFormField
            autoFocus
            onSubmitEditing={submitForm}
            onChange={(v) => setFieldValue("code", v)}
            name="promo-code"
            value={values.code}
            error={errors.code}
            label={fbt("Enter a promo code")}
          />
          <Spacer size={16} />
          <Column>
            <Button
              onPress={submitForm}
              disabled={!values.code}
              title={fbt("Apply")}
            />
          </Column>
        </div>
      </Dialog>
    </Container>
  );
}

interface TalkWithUsPromptProps {
  partnerID: string;
  messageableType?: OfferingMessageType;
  messageableID: string;
  hubspotForm?: React.ReactNode;
  hubspotForms?: {
    [key: string]: React.ReactNode;
  };
  talkWithUsExternalUrl?: string;
}

export function TalkWithUsPrompt(props: TalkWithUsPromptProps) {
  const {
    partnerID,
    messageableID,
    hubspotForm,
    messageableType = OfferingMessageType.PartnerOffering,
    hubspotForms,
    talkWithUsExternalUrl,
  } = props;
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<QuestionareData | null>(null);
  const hubspotFormToUse = useHubspotFormToUse(hubspotForms, hubspotForm);
  const { fbt } = useI18n();
  const weWorkExternalUrl = useWeWorkExternalContactUrl();
  const externalContactUrl = talkWithUsExternalUrl ?? weWorkExternalUrl;

  return (
    <Container>
      <Row alignItems="center" justifyContent="center">
        <Icon name="message-circle" />
        <Spacer size={8} direction="horizontal" />
        <Text align="center">{fbt("Not sure?")}</Text>
        <Spacer size={8} direction="horizontal" />
        <Text>
          {externalContactUrl ? (
            <TextLink
              href={externalContactUrl}
              external
              className="text-decoration-none text-primary talk-with-us__link btn btn-link"
              textDecoration="none"
            >
              {fbt("Talk with us")}
            </TextLink>
          ) : (
            <TextLink
              onPress={() => setOpen(true)}
              className="text-decoration-none text-primary talk-with-us__link btn btn-link"
              textDecoration="none"
            >
              {fbt("Talk with us")}
            </TextLink>
          )}
        </Text>
      </Row>
      <Dialog isOpen={open} onRequestClose={() => setOpen(false)}>
        <div className="talk-with-us-prompt-dialog-body">
          <Container height="100%">
            <Pressable onPress={() => setOpen(false)}>
              <Icon name="x" />
            </Pressable>
            <Spacer size={16} />
            {hubspotFormToUse ? (
              hubspotFormToUse
            ) : data ? (
              <LetsTalkConfirmation
                data={data}
                onClose={() => {
                  setOpen(false);
                  setData(null);
                }}
              />
            ) : (
              <Suspense fallback={<CenteredPulseLoader />}>
                <LetsTalkForm
                  partnerID={partnerID}
                  messageableID={messageableID}
                  onComplete={setData}
                  messageableType={messageableType}
                />
              </Suspense>
            )}
          </Container>
        </div>
      </Dialog>
    </Container>
  );
}

/**
 *
 * @TODO
 * This talk with us open close state can be controlled from anywhere
 * For now it will only be used on header for not to open multiple talk to use modals at the same time
 * Use this when same form just needs to be opened and closed, remove above component from unneeded places.
 */
export function TalkWithUsPromptWithContext(props: TalkWithUsPromptProps) {
  const {
    partnerID,
    messageableID,
    hubspotForm,
    messageableType = OfferingMessageType.PartnerOffering,
    hubspotForms,
    talkWithUsExternalUrl,
  } = props;
  const { isOpen, open, close } = useTalkToUsState();
  const [data, setData] = useState<QuestionareData | null>(null);
  const hubspotFormToUse = useHubspotFormToUse(hubspotForms, hubspotForm);
  const { fbt } = useI18n();
  const weWorkExternalUrl = useWeWorkExternalContactUrl();
  const externalContactUrl = talkWithUsExternalUrl ?? weWorkExternalUrl;

  return (
    <Container>
      <Row alignItems="center" justifyContent="center">
        <Icon name="message-circle" />
        <Spacer size={8} direction="horizontal" />
        <Text align="center">{fbt("Not sure?")}</Text>
        <Spacer size={8} direction="horizontal" />
        <Text>
          {externalContactUrl ? (
            <TextLink
              href={externalContactUrl}
              external
              className="text-decoration-none text-primary talk-with-us__link btn btn-link"
              textDecoration="none"
            >
              {fbt("Talk with us")}
            </TextLink>
          ) : (
            <TextLink
              onPress={open}
              className="text-decoration-none text-primary talk-with-us__link btn btn-link"
              textDecoration="none"
            >
              {fbt("Talk with us")}
            </TextLink>
          )}
        </Text>
      </Row>
      <Dialog isOpen={isOpen} onRequestClose={close}>
        <div className="talk-with-us-prompt-dialog-body">
          <Container height="100%">
            <Pressable onPress={close}>
              <Icon name="x" />
            </Pressable>
            <Spacer size={16} />
            {hubspotFormToUse ? (
              hubspotFormToUse
            ) : data ? (
              <LetsTalkConfirmation
                data={data}
                onClose={() => {
                  close();
                  setData(null);
                }}
              />
            ) : (
              <Suspense fallback={<CenteredPulseLoader />}>
                <LetsTalkForm
                  partnerID={partnerID}
                  messageableID={messageableID}
                  onComplete={setData}
                  messageableType={messageableType}
                />
              </Suspense>
            )}
          </Container>
        </div>
      </Dialog>
    </Container>
  );
}
